import {
  Suspense,
  lazy,
  type ComponentType,
  type LazyExoticComponent,
  type ReactElement,
} from "react";
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  isRouteErrorResponse,
  redirect,
  useLocation,
  useRouteError,
  type LoaderFunctionArgs,
} from "react-router-dom";
import DashboardLayout from "./app/dashboard/layout";
import InvestmentLayout from "./app/dashboard/investment/layout";
import { ApiError, api } from "./lib/api";

const LoginPage = lazy(() => import("./app/login/page"));
const DashboardPage = lazy(() => import("./app/dashboard/page"));
const CompaniesPage = lazy(() => import("./app/dashboard/companies/page"));
const InvestorsPage = lazy(() => import("./app/dashboard/investors/page"));
const VentureRequestsPage = lazy(() => import("./app/dashboard/venture-requests/page"));
const InvestmentOverviewPage = lazy(() => import("./app/dashboard/investment/page"));
const InvestmentActualsPage = lazy(() => import("./app/dashboard/investment/actuals/page"));
const CertificatesPage = lazy(() => import("./app/dashboard/investment/certificates/page"));
const EmailTemplatesPage = lazy(() => import("./app/dashboard/investment/email-templates/page"));
const FxRatesPage = lazy(() => import("./app/dashboard/investment/fx-rates/page"));
const LedgerPage = lazy(() => import("./app/dashboard/investment/ledger/page"));
const MailingPage = lazy(() => import("./app/dashboard/investment/mailing/page"));
const RoundsPage = lazy(() => import("./app/dashboard/investment/rounds/page"));
const RoundDetailPage = lazy(() => import("./app/dashboard/investment/rounds/[id]/page"));
const EditAllocationsPage = lazy(
  () => import("./app/dashboard/investment/rounds/[id]/allocations/edit/page"),
);
const EditRoundPage = lazy(() => import("./app/dashboard/investment/rounds/[id]/edit/page"));
const ForecastPage = lazy(() => import("./app/dashboard/investment/rounds/[id]/forecast/page"));
const HistoricalTicketsPage = lazy(() => import("./app/dashboard/investment/historical-tickets/page"));
const TicketPurchasesPage = lazy(() => import("./app/dashboard/investment/ticket-purchases/page"));
const TicketTypesPage = lazy(() => import("./app/dashboard/investment/ticket-types/page"));

function PageLoading() {
  return (
    <div className="flex min-h-[240px] items-center justify-center p-8" role="status" aria-live="polite">
      <div className="text-sm text-gray-600">Loading…</div>
    </div>
  );
}

function lazyElement(Component: LazyExoticComponent<ComponentType>): ReactElement {
  return (
    <Suspense fallback={<PageLoading />}>
      <Component />
    </Suspense>
  );
}

async function requireAdmin({ request }: LoaderFunctionArgs) {
  try {
    return await api.auth.me();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      const url = new URL(request.url);
      const destination = `${url.pathname}${url.search}`;
      throw redirect(`/login?redirect=${encodeURIComponent(destination)}`);
    }
    throw error;
  }
}

function DashboardShell() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

function InvestmentShell() {
  return (
    <InvestmentLayout>
      <Outlet />
    </InvestmentLayout>
  );
}

function PrefixRedirect({ from, to }: { from: string; to: string }) {
  const location = useLocation();
  const suffix = location.pathname.startsWith(from)
    ? location.pathname.slice(from.length)
    : "";
  const destination = `${to}${suffix}${location.search}${location.hash}`;
  return <Navigate to={destination} replace />;
}

function RouteErrorPage() {
  const error = useRouteError();
  let title = "Something went wrong";
  let message = "The admin console could not load this page.";

  if (isRouteErrorResponse(error)) {
    if (error.status === 403) {
      title = "Access denied";
      message = "Your account does not have permission to access this admin area.";
    } else if (error.status === 404) {
      title = "Page not found";
      message = "The requested admin page does not exist.";
    } else {
      message = error.statusText || message;
    }
  } else if (error instanceof Error) {
    message = error.message || message;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-lg rounded-lg border border-red-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-bold text-red-700">{title}</h1>
        <p className="mt-2 text-sm text-gray-600">{message}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Try again
          </button>
          <a
            href="/login"
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Return to login
          </a>
        </div>
      </div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
      <div className="text-center">
        <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">404</p>
        <h1 className="mt-2 text-3xl font-bold text-gray-900">Page not found</h1>
        <p className="mt-2 text-gray-600">The page you requested is not part of the admin console.</p>
        <a
          href="/dashboard"
          className="mt-6 inline-flex rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Go to dashboard
        </a>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: "/login",
    element: lazyElement(LoginPage),
    errorElement: <RouteErrorPage />,
  },
  {
    path: "/dashboard",
    loader: requireAdmin,
    element: <DashboardShell />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: lazyElement(DashboardPage) },
      { path: "companies", element: lazyElement(CompaniesPage) },
      { path: "investors", element: lazyElement(InvestorsPage) },
      { path: "venture-requests", element: lazyElement(VentureRequestsPage) },
      {
        path: "investment",
        element: <InvestmentShell />,
        children: [
          { index: true, element: lazyElement(InvestmentOverviewPage) },
          { path: "actuals", element: lazyElement(InvestmentActualsPage) },
          { path: "certificates", element: lazyElement(CertificatesPage) },
          { path: "email-templates", element: lazyElement(EmailTemplatesPage) },
          { path: "fx-rates", element: lazyElement(FxRatesPage) },
          { path: "ledger", element: lazyElement(LedgerPage) },
          { path: "mailing", element: lazyElement(MailingPage) },
          { path: "rounds", element: lazyElement(RoundsPage) },
          { path: "rounds/:id", element: lazyElement(RoundDetailPage) },
          { path: "rounds/:id/allocations/edit", element: lazyElement(EditAllocationsPage) },
          { path: "rounds/:id/edit", element: lazyElement(EditRoundPage) },
          { path: "rounds/:id/forecast", element: lazyElement(ForecastPage) },
          { path: "historical-tickets", element: lazyElement(HistoricalTicketsPage) },
          { path: "ticket-purchases", element: lazyElement(TicketPurchasesPage) },
          { path: "ticket-types", element: lazyElement(TicketTypesPage) },
        ],
      },
      {
        path: "rounds/*",
        element: (
          <PrefixRedirect
            from="/dashboard/rounds"
            to="/dashboard/investment/rounds"
          />
        ),
      },
      {
        path: "ticket-purchases",
        element: <Navigate to="/dashboard/investment/ticket-purchases" replace />,
      },
      {
        path: "ticket-types",
        element: <Navigate to="/dashboard/investment/ticket-types" replace />,
      },
      {
        path: "finance",
        element: <Navigate to="/dashboard/investment/ledger" replace />,
      },
    ],
  },
  {
    path: "/investment/*",
    element: <PrefixRedirect from="/investment" to="/dashboard/investment" />,
  },
  {
    path: "/rounds/*",
    element: <PrefixRedirect from="/rounds" to="/dashboard/investment/rounds" />,
  },
  {
    path: "/ticket-purchases",
    element: <Navigate to="/dashboard/investment/ticket-purchases" replace />,
  },
  {
    path: "/ticket-types",
    element: <Navigate to="/dashboard/investment/ticket-types" replace />,
  },
  {
    path: "/finance",
    element: <Navigate to="/dashboard/investment/ledger" replace />,
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
]);
